import { useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { CallsignRail } from './CallsignRail'
import { stableAvatarSrc } from './stableAvatarSrc'

type GroupAvatarMember = { name: string; photoUrl?: string | null; photoFileId?: string | null }

const CELL_COLORS = ['bg-brand-accent-soft text-brand-accent-ink', 'bg-amber-100 text-amber-800', 'bg-emerald-100 text-emerald-800', 'bg-violet-100 text-violet-800']

function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  return ((words[0]?.[0] ?? '') + (words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?'
}

function Cell({ member, index, compact }: { member: GroupAvatarMember; index: number; compact: boolean }) {
  const [failed, setFailed] = useState(false)
  const src = stableAvatarSrc(member.photoFileId, member.photoUrl)
  if (src && !failed) {
    return <img src={src} alt="" onError={() => setFailed(true)} className="h-full w-full object-cover" />
  }
  return (
    <span
      className={cn(
        'flex h-full w-full items-center justify-center font-semibold',
        compact ? 'text-[8.5px]' : 'text-[11px]',
        CELL_COLORS[index % CELL_COLORS.length],
      )}
    >
      {compact ? initialsOf(member.name).slice(0, 1) : initialsOf(member.name)}
    </span>
  )
}

export function GroupAvatar({
  members,
  callsign,
  className,
}: {
  members: GroupAvatarMember[]
  callsign: string
  className?: string
}) {
  const shown = members.slice(0, 4)
  if (shown.length === 0) return <CallsignRail callsign={callsign} className={className} />

  const layout =
    shown.length === 1
      ? 'grid-cols-1 grid-rows-1'
      : shown.length === 2
        ? 'grid-cols-2 grid-rows-1'
        : 'grid-cols-2 grid-rows-2'

  return (
    <span
      className={cn(
        'grid h-[34px] w-[34px] shrink-0 gap-px overflow-hidden rounded-lg bg-line-2',
        layout,
        className,
      )}
    >
      {shown.map((member, index) => (
        <span
          key={`${member.name}-${index}`}
          className={cn('overflow-hidden', shown.length === 3 && index === 0 && 'row-span-2')}
        >
          <Cell member={member} index={index} compact={shown.length > 1} />
        </span>
      ))}
    </span>
  )
}
